import { datadogLogs } from '@datadog/browser-logs';
import { AUTH_SSO_SAML } from 'business/constants';
import type { VisionState } from 'components/App/types';
import { VISION_PATH } from 'env';
import { trackEvent } from 'helpers/trackerHelpers';
import i18n from 'i18n';
import {
    captureSSOLoginError,
    getSSOErrorDetails,
} from 'routes/LoginPage/sso/helpers';
import { snackbarService } from 'shared/lib/snackbar/service';

import { getSSOAccessToken } from './sso/msal';
import type { AuthResponse } from './types';

type LoginPageState = {
    showResetPasswordForm: boolean;
};

const loginPageState: LoginPageState = {
    showResetPasswordForm: false,
};

const loginPageModel = {
    state: loginPageState,
    reducers: {
        toggleResetPasswordForm(state: LoginPageState): LoginPageState {
            return {
                ...state,
                showResetPasswordForm: !state.showResetPasswordForm,
            };
        },
    },
    // dispatch is 'any' until we upgrade rematch to correctly type it
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    effects: (dispatch: any) => ({
        async login(payload: {
            email: string;
            password: string;
            rememberMe: boolean;
        }) {
            trackEvent('login-attempt', { email: payload.email });
            return dispatch.API.post({
                params: {
                    endpoint: 'auth/login',
                    body: {
                        email: payload.email.toLowerCase(),
                        password: payload.password,
                        rememberMe: payload.rememberMe,
                    },
                },
                // @ts-expect-error [TS migration] Was not detected by flow
                successAction: (response) => {
                    trackEvent('login-success', { email: payload.email });
                    dispatch.vision.setAuthenticatedState(response);
                },
                // @ts-expect-error [TS migration] Was not detected by flow
                failureAction: (status, response) => {
                    trackEvent('login-failed', { email: payload.email });
                    const message =
                        status === 429
                            ? 'login.tooManyRequests'
                            : response?.message;
                    snackbarService.showWarning(i18n.t(message));
                },
            });
        },

        async ssoLogin(_payload: unknown, rootState: { vision: VisionState }) {
            const { authMethods, bind } = rootState.vision.client;

            try {
                // For SAML SSO
                if (authMethods.includes(AUTH_SSO_SAML)) {
                    trackEvent('login-attempt-sso');
                    const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                    window.location.href = `${VISION_PATH}/auth/saml/${bind}/login?redirect=${encodeURIComponent(redirect)}`;
                    return null;
                }

                // For OpenID Connect SSO
                const accessToken = await getSSOAccessToken(bind);
                trackEvent('login-attempt-sso');

                return dispatch.API.post({
                    params: {
                        endpoint: 'auth/sso_openidc_login',
                        body: { access_token: accessToken },
                    },

                    successAction: (response: AuthResponse) => {
                        trackEvent('login-success-sso');
                        dispatch.vision.setAuthenticatedState(response);
                    },
                    failureAction: (status: number, response: AuthResponse) => {
                        captureSSOLoginError({
                            bind,
                            error: new Error(
                                'User failed to exchange SSO token with Manty API.',
                            ),
                            stage: 'manty_token_exchange',
                            status,
                            response,
                        });
                        snackbarService.showWarning(
                            i18n.t('login.ssoLoginError'),
                        );
                    },
                });
            } catch (error) {
                const errorDetails = getSSOErrorDetails(error);
                const serializedError = [
                    errorDetails.errorMessage,
                    errorDetails.message,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                let message = i18n.t('login.ssoLoginError');

                if (serializedError.includes('interaction_in_progress')) {
                    message = i18n.t('login.interactionInProgress');
                }
                if (serializedError.includes('invalid_client')) {
                    message = i18n.t('login.ssoLoginErrorOrTokenExpired');
                }
                if (serializedError.includes('timed_out')) {
                    message = i18n.t('login.ssoLoginTimedOut');
                }
                datadogLogs.logger.warn(
                    `Error in SSO process ${errorDetails.errorCode}`,
                );

                snackbarService.showWarning(message);
                if (
                    !serializedError.includes('user_cancelled') &&
                    !serializedError.includes('timed_out') &&
                    !serializedError.includes('interaction_in_progress')
                ) {
                    captureSSOLoginError({
                        bind,
                        error,
                        stage: 'msal',
                    });
                }
                return null;
            }
        },

        async requestResetPassword(payload: { email: string }) {
            trackEvent('password-reset-requested', { email: payload.email });
            return dispatch.API.post({
                params: {
                    endpoint: 'auth/request_reset_password',
                    body: { email: payload.email.toLowerCase() },
                },
                successAction: () => {
                    dispatch.loginPage.toggleResetPasswordForm();
                },
                failureAction: () => {
                    dispatch.loginPage.toggleResetPasswordForm();
                },
            });
        },
    }),
};

export default loginPageModel;
